import React from 'react';
import type { FC } from 'react';
import { useBiLogger } from '@wix/cli-telemetry-react';
import type { DeveloperApp } from '@wix/dev-center-client';
import type { RepoType } from '@wix/package-manager';
import { Box, Spinner, useAsyncCallback } from '@wix/cli-ui-kit';
import { useDevCenterClient } from '@wix/dev-center-client';
import { useBackendAsAServiceClient } from '@wix/backend-as-a-service-client';
import { match } from 'variant';
import { useTranslation } from '@wix/cli-i18n';
import { TemplateSource } from '../../parse-command-options.js';
import type { AppData, GenerationData } from '../../types.js';
import { ChoosePackageName } from '../ChoosePackageName.js';
import { ExtendExistingApp } from '../ExtendExistingApp.js';
import { McpConfigResult } from '../mcp/getMcpResult.js';
import { createAppProjectAndEnvironment } from '../../services/project-setup.js';

interface ExistingAppFlowProps {
  targetParentFolder: string;
  repoType: RepoType;
  developerApps: DeveloperApp[];
  onSubmit: (data: GenerationData) => void;
}

interface RegisterAppCallbackArgs {
  appData: AppData;
  packageName: string;
}

export const ExistingAppFlow: FC<ExistingAppFlowProps> = ({
  onSubmit,
  developerApps,
  targetParentFolder,
  repoType,
}) => {
  const devCenterClient = useDevCenterClient();
  const backendAsAServiceClient = useBackendAsAServiceClient();
  const biLogger = useBiLogger();
  const { t } = useTranslation();

  const getAppCallback = useAsyncCallback(
    async (_, { appId, appName }: { appId: string; appName: string }) => {
      const app = await devCenterClient.getApp({ appId });
      return {
        appId,
        appName,
        namespace: app.namespace,
        codeIdentifier: app.codeIdentifier,
      } satisfies AppData;
    }
  );

  const registerAppCallback = useAsyncCallback(
    async (_, { appData, packageName }: RegisterAppCallbackArgs) => {
      const environmentVariables = await createAppProjectAndEnvironment({
        appId: appData.appId,
        appName: appData.appName,
        devCenterClient,
        backendAsAServiceClient,
      });

      onSubmit({
        appData,
        packageName,
        template: TemplateSource.Default(),
        mcpConfigResult: McpConfigResult.Canceled(),
        environmentVariables,
      });
    }
  );

  return (
    <>
      <ExtendExistingApp
        developerApps={developerApps}
        onSelected={({ appId, appName }) => {
          biLogger.updateDefaults({
            _appId: appId,
          });
          void getAppCallback.execute({ appId, appName });
        }}
      />

      {match(getAppCallback.status, {
        NotRequested: () => null,
        Loading: () => (
          <Box marginTop={1}>
            <Spinner text={t('create_app.extend_existing.fetch_app_details')} />
          </Box>
        ),
        Error: () => null,
        Success: ({ result: appData }) => (
          <>
            <ChoosePackageName
              appName={appData.appName}
              targetParentFolder={targetParentFolder}
              repoType={repoType}
              onSelected={(packageName) => {
                void registerAppCallback.execute({
                  appData,
                  packageName,
                });
              }}
            />
            {match(registerAppCallback.status, {
              NotRequested: () => null,
              Loading: () => (
                <Box marginTop={1}>
                  <Spinner text={t('create_app.extend_existing.get_project')} />
                </Box>
              ),
              Error: () => null,
              Success: () => null,
            })}
          </>
        ),
      })}
    </>
  );
};
